import { type DialogProps } from "@radix-ui/react-dialog";
import { Command } from "cmdk";
import { type KeyboardEvent, useMemo } from "react";

import { Dialog } from "../../components/dialog";
import { type CommandContext, CommandContextProvider } from "./context";

export type CommandRootProps = Omit<DialogProps, "onOpenChange"> & {
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
  shouldFilter?: boolean;
};

// cmdk selects the highlighted row on Enter without clicking it, so a link row wouldn't follow its link: click it
// instead, with Enter's modifiers, as a click would (in a new tab for ⌘/Ctrl, a new window for Shift, …). The click
// selects the row as well.
const clickHighlightedLink = (event: KeyboardEvent<HTMLDivElement>) => {
  if (event.key !== "Enter" || event.nativeEvent.isComposing) {
    return;
  }
  const item = event.currentTarget.querySelector('[cmdk-item][aria-selected="true"]');
  if (!(item instanceof HTMLAnchorElement)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  const { altKey, ctrlKey, metaKey, shiftKey } = event;
  item.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, altKey, ctrlKey, metaKey, shiftKey }));
};

const Root = ({
  children,
  isLoading = false,
  defaultOpen,
  modal,
  onOpenChange,
  open,
  shouldFilter,
}: CommandRootProps) => {
  const value = useMemo<CommandContext>(() => ({ isLoading }), [isLoading]);
  return (
    <CommandContextProvider value={value}>
      <Dialog.Root defaultOpen={defaultOpen} modal={modal} onOpenChange={onOpenChange} open={open}>
        <Dialog.Content className="w-160 max-w-160 overflow-hidden border border-neutral-80 p-0">
          <Command
            className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5"
            shouldFilter={shouldFilter}
            // Before cmdk's own handler, which would select the row without clicking it.
            onKeyDownCapture={clickHighlightedLink}
          >
            {children}
          </Command>
        </Dialog.Content>
      </Dialog.Root>
    </CommandContextProvider>
  );
};

export default Root;
